import { Activity, CreditCard, PieChart } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { centsToCurrency } from "../../utils/money";

interface InsurancePendingCardProps {
  lang: "pt" | "en";
  pendingInsuranceCount: number;
  onOpenBatchDrawer: () => void;
  t: any;
}

export function InsurancePendingCard({ lang, pendingInsuranceCount, onOpenBatchDrawer, t }: InsurancePendingCardProps) {
  return (
    <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-neutral-900">
              {t.summary.insurancePayments}
            </h3>
            <p className="text-xs text-neutral-500 font-medium">
              {pendingInsuranceCount} {t.summary.invoicesWaiting}
            </p>
          </div>
        </div>
        <Button 
          variant="primary"
          className="h-10 px-6 font-bold"
          onClick={onOpenBatchDrawer}
        >
          {t.summary.batchAction}
        </Button>
      </div>
    </div>
  );
}

interface FinancialSummaryProps {
  lang: "pt" | "en";
  stats: {
    incomes: number;
    expenses: number;
    balance: number;
    projection: number;
  };
  pendingInsuranceCount: number;
  onOpenBatchDrawer: () => void;
  dynamicBreakdown: [string, number][];
  methodBreakdown: { method: string; total: number }[];
  t: any;
}

export function FinancialSummary({
  lang,
  stats,
  pendingInsuranceCount,
  onOpenBatchDrawer,
  dynamicBreakdown,
  methodBreakdown,
  t,
}: FinancialSummaryProps) {
  return (
    <div className="flex flex-col gap-8">
      {/* Top Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-1">
        {/* Entradas */}
        <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm">
          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">
            {t.summary.incomes}
          </p>
          <div className="flex items-baseline gap-1">
            <h3 className="text-2xl font-bold text-green-600">
              {centsToCurrency(stats.incomes, lang)}
            </h3>
          </div>
        </div>

        {/* Saídas */}
        <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm">
          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">
            {t.summary.expenses}
          </p>
          <div className="flex items-baseline gap-1">
            <h3 className="text-2xl font-bold text-red-600">
              {centsToCurrency(stats.expenses, lang)}
            </h3>
          </div>
        </div>

        {/* Resultado */}
        <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm">
          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">
            {t.summary.netResult}
          </p>
          <div className="flex items-baseline gap-1">
            <h3 className={`text-2xl font-bold ${stats.balance >= 0 ? 'text-neutral-900' : 'text-red-600'}`}>
              {centsToCurrency(stats.balance, lang)}
            </h3>
          </div>
        </div>

        {/* Projeção */}
        <div className="bg-neutral-900 p-6 rounded-xl shadow-lg border border-neutral-800">
          <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest mb-1">
            {t.summary.forecast}
          </p>
          <h3 className="text-2xl font-bold text-white">
            {centsToCurrency(stats.projection, lang)}
          </h3>
        </div>
      </div>

      {/* Extra Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-sm border-neutral-100 overflow-hidden">
          <CardHeader className="bg-neutral-50/50 py-3 px-6 border-b border-neutral-100">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-neutral-500">
                {t.breakdown.specialty}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-neutral-50">
              {dynamicBreakdown.length === 0 ? (
                <div className="px-6 py-8 text-center text-xs text-neutral-400 italic">
                  {t.empty}
                </div>
              ) : (
                dynamicBreakdown.map(([name, total]) => (
                  <div key={name} className="flex items-center justify-between px-6 py-3 hover:bg-neutral-50/30 transition-colors">
                    <span className="text-sm font-medium text-neutral-700">{name}</span>
                    <span className="text-sm font-bold text-neutral-900">{centsToCurrency(total, lang)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-neutral-100 overflow-hidden">
          <CardHeader className="bg-neutral-50/50 py-3 px-6 border-b border-neutral-100">
            <div className="flex items-center gap-2">
              <PieChart className="h-4 w-4 text-green-600" />
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-neutral-500">
                {t.breakdown.method}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-neutral-50">
              {methodBreakdown.length === 0 ? (
                <div className="px-6 py-8 text-center text-xs text-neutral-400 italic">
                  {t.empty}
                </div>
              ) : (
                methodBreakdown.map(({ method, total }) => (
                  <div key={method} className="flex items-center justify-between px-6 py-3 hover:bg-neutral-50/30 transition-colors">
                    <span className="text-sm font-medium text-neutral-700">{method}</span>
                    <span className="text-sm font-bold text-neutral-900">{centsToCurrency(total, lang)}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
